"use client";

import { c as _c } from "react/compiler-runtime";
import { publicEnv } from "@/config/env";
import { faCopy, faExternalLink } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ShareIcon } from "@heroicons/react/24/outline";
import yaml from "js-yaml";
import Image from "next/image";
import { usePathname, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { Tooltip } from "react-tooltip";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useCapacitor from "@/hooks/useCapacitor";
import { DeepLinkScope } from "@/hooks/useDeepLinkNavigation";
import { useElectron } from "@/hooks/useElectron";
import { getRefreshToken, getWalletAddress, getWalletRole } from "@/services/auth/auth.utils";
import { useSeizeConnectContext } from "@/components/auth/SeizeConnectContext";
import { ShareMobileApp } from "./HeaderShareMobileApps";
const QRCode = require("qrcode");
interface OSInfo {
  name: "windows" | "mac" | "linux";
  url: string;
  displayName: string;
  downloadPath: string;
  image: string;
  enabled: boolean;
  version?: string | undefined;
}
interface FileData {
  url: string;
  sha512: string;
  size: number;
}
interface LatestYml {
  version: string;
  files: FileData[];
}
const CORE_OS_CONFIGS: OSInfo[] = [{
  name: "windows",
  url: "https://6529bucket.s3.eu-west-1.amazonaws.com/6529-core-app/win/latest.yml",
  displayName: "Windows",
  downloadPath: "6529-core-app/win/links",
  image: "/windows.png",
  enabled: true
}, {
  name: "mac",
  url: "https://6529bucket.s3.eu-west-1.amazonaws.com/6529-core-app/mac/latest-mac.yml",
  displayName: "macOS",
  downloadPath: "6529-core-app/mac/links",
  image: "/macos.png",
  enabled: true
}, {
  name: "linux",
  url: "https://6529bucket.s3.eu-west-1.amazonaws.com/6529-core-app/linux/latest-linux.yml",
  displayName: "Linux",
  downloadPath: "6529-core-app/linux/links",
  image: "/linux.png",
  enabled: true
}];
const bodyScrollLock = (() => {
  let lockCount = 0;
  let previousOverflow = "";
  return {
    lock: () => {
      if (typeof document === "undefined") {
        return;
      }
      if (lockCount === 0) {
        previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
      }
      lockCount += 1;
    },
    unlock: () => {
      if (typeof document === "undefined" || lockCount === 0) {
        return;
      }
      lockCount -= 1;
      if (lockCount === 0) {
        document.body.style.overflow = previousOverflow;
      }
    }
  };
})();
enum Mode {
  NAVIGATE,
  SHARE,
  APPS,
}
enum SubMode {
  BROWSER,
  APP,
  CORE,
}
const squareStyle = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
};
function getSubTabCount(activeTab: Mode, isElectron: boolean): number {
  if (activeTab === Mode.NAVIGATE) {
    return isElectron ? 2 : 3;
  }
  return isElectron ? 1 : 2;
}
function getSubTabLabel(activeTab: Mode): string {
  if (activeTab === Mode.APPS) {
    return "Select Platform";
  }
  if (activeTab === Mode.SHARE) {
    return "Open Link In";
  }
  return "Open URL In";
}
function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const selectors = ['a[href]:not([tabindex="-1"])', 'button:not([disabled]):not([tabindex="-1"])', 'textarea:not([disabled]):not([tabindex="-1"])', 'input:not([disabled]):not([tabindex="-1"])', 'select:not([disabled]):not([tabindex="-1"])', '[tabindex]:not([tabindex="-1"])'].join(",");
  return Array.from(container.querySelectorAll<HTMLElement>(selectors)).filter(element => !element.hasAttribute("disabled") && element.getAttribute("aria-hidden") !== "true");
}
export default function HeaderShare(t0) {
  const $ = _c(18);
  const {
    isCollapsed: t1
  } = t0;
  const isCollapsed = t1 === undefined ? false : t1;
  const capacitor = useCapacitor();
  const isMobileDevice = useIsMobileDevice();
  const [showQRModal, setShowQRModal] = useState(false);
  if (capacitor.isCapacitor || isMobileDevice) {
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <></>;
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    return t2;
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setShowQRModal(true);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `tw-block tw-h-[2.875rem] tw-w-full tw-cursor-pointer tw-rounded-xl tw-border-none tw-bg-transparent tw-px-2 tw-text-left tw-text-base tw-font-medium tw-text-iron-400 tw-no-underline tw-transition-colors tw-duration-200 focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-iron-500 focus-visible:tw-ring-offset-2 ${isCollapsed ? "desktop-hover:hover:tw-text-white" : "desktop-hover:hover:tw-bg-iron-900 desktop-hover:hover:tw-text-white"} active:tw-bg-transparent`;
  const t4 = !isCollapsed;
  const t5 = `tw-flex tw-h-full tw-w-full tw-items-center ${isCollapsed ? "" : "tw-gap-x-2"}`;
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="tw-flex tw-w-10 tw-flex-shrink-0 tw-items-center tw-justify-center"><ShareIcon className="tw-h-6 tw-w-6 tw-flex-shrink-0" /></div>;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const t7 = `tw-block tw-overflow-hidden tw-whitespace-nowrap tw-transition-all tw-duration-300 ${isCollapsed ? "tw-w-0 tw-opacity-0" : "tw-flex-1 tw-opacity-100"}`;
  let t8;
  if ($[3] !== t7) {
    t8 = <span className={t7}>Share</span>;
    $[3] = t7;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  let t9;
  if ($[5] !== t5 || $[6] !== t8) {
    t9 = <div className={t5}>{t6}{t8}</div>;
    $[5] = t5;
    $[6] = t8;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== t3 || $[9] !== t4 || $[10] !== t9) {
    t10 = <button type="button" aria-label="QR Code" title="QR Code" onClick={t2} className={t3} data-tooltip-id="sidebar-tooltip" data-tooltip-content="Share" data-tooltip-hidden={t4}>{t9}</button>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => setShowQRModal(false);
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== showQRModal) {
    t12 = <HeaderQRModal show={showQRModal} onClose={t11} />;
    $[13] = showQRModal;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== t10 || $[16] !== t12) {
    t13 = <div className="tailwind-scope tw-relative tw-px-3">{t10}{t12}</div>;
    $[15] = t10;
    $[16] = t12;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  return t13;
}
export function HeaderQRModal(t0) {
  const $ = _c(95);
  const {
    show,
    onClose
  } = t0;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isMobile = useIsMobileDevice();
  const [shouldRender, setShouldRender] = useState(show);
  const [isVisible, setIsVisible] = useState(show);
  const {
    isAuthenticated
  } = useSeizeConnectContext();
  const [activeTab, setActiveTab] = useState(isAuthenticated ? Mode.SHARE : Mode.NAVIGATE);
  const [activeSubTab, setActiveSubTab] = useState(SubMode.APP);
  const [navigateBrowserUrl, setNavigateBrowserUrl] = useState("");
  const [navigateAppUrl, setNavigateAppUrl] = useState("");
  const [shareConnectionAppUrl, setShareConnectionAppUrl] = useState("");
  const [navigateCoreUrl, setNavigateCoreUrl] = useState("");
  const [shareConnectionCoreUrl, setShareConnectionCoreUrl] = useState("");
  const [navigateBrowserSrc, setNavigateBrowserSrc] = useState("");
  const [navigateAppSrc, setNavigateAppSrc] = useState("");
  const [shareConnectionSrc, setShareConnectionSrc] = useState("");
  const [urlCopied, setUrlCopied] = useState(false);
  const onCloseRef = useRef(onClose);
  const copyTimeoutRef = useRef(null);
  const dialogRef = useRef(null);
  const previouslyFocusedElementRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = event => {
      if (event.key !== "Tab") {
        return;
      }
      const dialog = dialogRef.current;
      if (!dialog) {
        return;
      }
      const focusableElements = getFocusableElements(dialog);
      if (focusableElements.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const firstElement = focusableElements[0];
      const lastElement = focusableElements.at(-1);
      if (!firstElement || !lastElement) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const activeElement = document.activeElement as HTMLElement | null;
      const activeInsideDialog = activeElement ? dialog.contains(activeElement) : false;
      if (event.shiftKey) {
        if (!activeInsideDialog || activeElement === firstElement || activeElement === dialog) {
          event.preventDefault();
          lastElement.focus();
        }
        return;
      }
      if (!activeInsideDialog || activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const trapFocusInDialog = t1;
  let t2;
  let t3;
  if ($[1] !== onClose) {
    t2 = () => {
      onCloseRef.current = onClose;
    };
    t3 = [onClose];
    $[1] = onClose;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
        copyTimeoutRef.current = null;
      }
    };
    t5 = [];
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = event_0 => {
      if (event_0.key === "Tab") {
        trapFocusInDialog(event_0);
        return;
      }
      if (event_0.key === "Escape") {
        event_0.stopPropagation();
        event_0.preventDefault();
        onCloseRef.current();
      }
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const handleEscapeKeyDown = t6;
  let t7;
  if ($[7] !== pathname || $[8] !== searchParams) {
    t7 = function generateSources(refreshToken, walletAddress, role) {
      let routerPath = pathname ?? "";
      if (routerPath.endsWith("/")) {
        routerPath = routerPath.slice(0, -1);
      }
      const searchParamsString = searchParams?.toString() ?? "";
      if (searchParamsString) {
        routerPath = routerPath + `?${searchParamsString}`;
      }
      const appScheme = publicEnv.MOBILE_APP_SCHEME ?? "mobile6529";
      const coreScheme = publicEnv.CORE_SCHEME ?? "core6529";
      const browserUrl = `${window.location.origin}${routerPath}`;
      const appUrl = `${appScheme}://${DeepLinkScope.NAVIGATE}${routerPath}`;
      const coreUrl = `${coreScheme}://${DeepLinkScope.NAVIGATE}${routerPath}`;
      setNavigateBrowserUrl(browserUrl);
      setNavigateAppUrl(appUrl);
      setNavigateCoreUrl(coreUrl);
      let shareConnectionAppUrl_0 = "";
      let shareConnectionCoreUrl_0;
      if (refreshToken && walletAddress) {
        shareConnectionAppUrl_0 = `${appScheme}://${DeepLinkScope.SHARE_CONNECTION}?token=${refreshToken}&address=${walletAddress}`;
        shareConnectionCoreUrl_0 = `${coreScheme}://${DeepLinkScope.NAVIGATE}/accept-connection-sharing?token=${refreshToken}&address=${walletAddress}`;
        if (role) {
          shareConnectionAppUrl_0 = shareConnectionAppUrl_0 + `&role=${role}`;
          shareConnectionCoreUrl_0 = shareConnectionCoreUrl_0 + `&role=${role}`;
        }
        setShareConnectionAppUrl(shareConnectionAppUrl_0);
        setShareConnectionCoreUrl(shareConnectionCoreUrl_0);
      } else {
        setShareConnectionSrc("");
      }
      QRCode.toDataURL(browserUrl, {
        width: 500,
        margin: 0
      }).then(dataUrl => {
        setNavigateBrowserSrc(dataUrl);
      }).catch(error => {
        console.error("Failed to generate browser QR code", error);
        setNavigateBrowserSrc("");
      });
      QRCode.toDataURL(appUrl, {
        width: 500,
        margin: 0
      }).then(dataUrl_0 => {
        setNavigateAppSrc(dataUrl_0);
      }).catch(error_0 => {
        console.error("Failed to generate mobile app QR code", error_0);
        setNavigateAppSrc("");
      });
      if (shareConnectionAppUrl_0) {
        QRCode.toDataURL(shareConnectionAppUrl_0, {
          width: 500,
          margin: 0
        }).then(dataUrl_1 => {
          setShareConnectionSrc(dataUrl_1);
        }).catch(error_1 => {
          console.error("Failed to generate share connection QR code", error_1);
          setShareConnectionSrc("");
        });
      }
    };
    $[7] = pathname;
    $[8] = searchParams;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const generateSources = t7;
  let t8;
  if ($[10] !== generateSources || $[11] !== show) {
    t8 = () => {
      if (show) {
        generateSources(getRefreshToken(), getWalletAddress(), getWalletRole());
      }
    };
    $[10] = generateSources;
    $[11] = show;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== show) {
    t9 = [show];
    $[13] = show;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[15] !== isAuthenticated || $[16] !== show) {
    t10 = () => {
      setActiveTab(isAuthenticated ? Mode.SHARE : Mode.NAVIGATE);
      setActiveSubTab(SubMode.APP);
      if (show) {
        return;
      }
      const timer = setTimeout(() => {
        setNavigateBrowserSrc("");
        setNavigateAppSrc("");
        setShareConnectionSrc("");
      }, 150);
      return () => clearTimeout(timer);
    };
    t11 = [show, isAuthenticated];
    $[15] = isAuthenticated;
    $[16] = show;
    $[17] = t10;
    $[18] = t11;
  } else {
    t10 = $[17];
    t11 = $[18];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[19] !== show) {
    t12 = () => {
      if (show) {
        setShouldRender(true);
        const raf = requestAnimationFrame(() => setIsVisible(true));
        return () => cancelAnimationFrame(raf);
      }
      setIsVisible(false);
      const timeout = setTimeout(() => setShouldRender(false), 200);
      return () => clearTimeout(timeout);
    };
    t13 = [show];
    $[19] = show;
    $[20] = t12;
    $[21] = t13;
  } else {
    t12 = $[20];
    t13 = $[21];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[22] !== shouldRender) {
    t14 = () => {
      if (!shouldRender) {
        return;
      }
      bodyScrollLock.lock();
      globalThis.addEventListener("keydown", handleEscapeKeyDown);
      return () => {
        bodyScrollLock.unlock();
        globalThis.removeEventListener("keydown", handleEscapeKeyDown);
      };
    };
    t15 = [shouldRender, handleEscapeKeyDown];
    $[22] = shouldRender;
    $[23] = t14;
    $[24] = t15;
  } else {
    t14 = $[23];
    t15 = $[24];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[25] !== shouldRender) {
    t16 = () => {
      if (!shouldRender) {
        return;
      }
      const activeElement_0 = document.activeElement;
      previouslyFocusedElementRef.current = activeElement_0 instanceof HTMLElement ? activeElement_0 : null;
      const raf_0 = requestAnimationFrame(() => {
        const dialog_0 = dialogRef.current;
        if (!dialog_0) {
          return;
        }
        const focusableElements_0 = getFocusableElements(dialog_0);
        const firstFocusableElement = focusableElements_0[0];
        if (firstFocusableElement) {
          firstFocusableElement.focus();
          return;
        }
        dialog_0.focus();
      });
      return () => {
        cancelAnimationFrame(raf_0);
        previouslyFocusedElementRef.current?.focus();
      };
    };
    t17 = [shouldRender];
    $[25] = shouldRender;
    $[26] = t16;
    $[27] = t17;
  } else {
    t16 = $[26];
    t17 = $[27];
  }
  useEffect(t16, t17);
  const renderQRCodeImage = _temp;
  const renderCoreLink = _temp2;
  let t18;
  if ($[28] !== activeSubTab || $[29] !== navigateAppSrc || $[30] !== navigateAppUrl || $[31] !== navigateBrowserSrc || $[32] !== navigateBrowserUrl || $[33] !== navigateCoreUrl) {
    t18 = () => {
      if (activeSubTab === SubMode.BROWSER) {
        return {
          content: renderQRCodeImage(navigateBrowserSrc, "Browser Link - QR Code"),
          url: navigateBrowserUrl
        };
      }
      if (activeSubTab === SubMode.CORE) {
        return {
          content: renderCoreLink(navigateCoreUrl),
          url: navigateCoreUrl
        };
      }
      return {
        content: renderQRCodeImage(navigateAppSrc, "Mobile App Link - QR Code"),
        url: navigateAppUrl
      };
    };
    $[28] = activeSubTab;
    $[29] = navigateAppSrc;
    $[30] = navigateAppUrl;
    $[31] = navigateBrowserSrc;
    $[32] = navigateBrowserUrl;
    $[33] = navigateCoreUrl;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  const getNavigateContent = t18;
  let t19;
  if ($[35] !== activeSubTab || $[36] !== shareConnectionAppUrl || $[37] !== shareConnectionCoreUrl || $[38] !== shareConnectionSrc) {
    t19 = () => {
      if (activeSubTab === SubMode.CORE) {
        return {
          content: renderCoreLink(shareConnectionCoreUrl),
          url: shareConnectionCoreUrl
        };
      }
      if (activeSubTab === SubMode.APP) {
        return {
          content: renderQRCodeImage(shareConnectionSrc, "Share Connection - QR Code"),
          url: shareConnectionAppUrl
        };
      }
      return {
        content: <span>Invalid submode for SHARE</span>,
        url: ""
      };
    };
    $[35] = activeSubTab;
    $[36] = shareConnectionAppUrl;
    $[37] = shareConnectionCoreUrl;
    $[38] = shareConnectionSrc;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  const getShareContent = t19;
  let t20;
  if ($[40] !== activeSubTab) {
    t20 = () => {
      if (activeSubTab === SubMode.CORE) {
        return {
          content: <CoreAppsDownload />,
          url: ""
        };
      }
      return {
        content: <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-12 tw-p-10" style={squareStyle}><ShareMobileApp platform="ios" /><ShareMobileApp platform="android" /></div>,
        url: ""
      };
    };
    $[40] = activeSubTab;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  const getAppsContent = t20;
  let t21;
  if ($[42] !== activeTab || $[43] !== getAppsContent || $[44] !== getNavigateContent || $[45] !== getShareContent) {
    t21 = () => {
      if (activeTab === Mode.NAVIGATE) {
        return getNavigateContent();
      }
      if (activeTab === Mode.SHARE) {
        return getShareContent();
      }
      return getAppsContent();
    };
    $[42] = activeTab;
    $[43] = getAppsContent;
    $[44] = getNavigateContent;
    $[45] = getShareContent;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  const getDisplayContent = t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t26;
  let t27;
  let t28;
  let t29;
  let t30;
  let t31;
  let t32;
  let t33;
  let t34;
  let t35;
  if ($[47] !== activeSubTab || $[48] !== activeTab || $[49] !== getDisplayContent || $[50] !== isMobile || $[51] !== isVisible || $[52] !== onClose || $[53] !== shouldRender || $[54] !== urlCopied) {
    t35 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const printImage = function printImage() {
        const {
          content,
          url: url_0
        } = getDisplayContent();
        return <div className="tw-flex tw-flex-col tw-gap-2"><div className="tw-relative tw-aspect-square tw-w-full tw-overflow-hidden"><div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">{content}</div></div>{url_0 ? <div className="tw-flex tw-h-10 tw-items-center tw-gap-2 tw-rounded-lg tw-bg-iron-900 tw-px-3"><div className="tw-min-w-0 tw-flex-1 tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-text-sm tw-text-iron-400" title={url_0}>{url_0}</div><button type="button" aria-label="Copy URL" className="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-border-0 tw-bg-transparent tw-text-iron-400 tw-transition-colors hover:tw-bg-iron-800 hover:tw-text-iron-100" data-tooltip-id="copy-url-tooltip" onClick={async () => {
              ;
              try {
                await navigator.clipboard.writeText(url_0);
                setUrlCopied(true);
                if (copyTimeoutRef.current) {
                  clearTimeout(copyTimeoutRef.current);
                }
                copyTimeoutRef.current = setTimeout(() => {
                  setUrlCopied(false);
                  copyTimeoutRef.current = null;
                }, 500);
              } catch (t36) {
                const error_2 = t36;
                console.error("Failed to copy share URL to clipboard", error_2);
              }
            }}><FontAwesomeIcon icon={faCopy} className={urlCopied ? "tw-text-green-500" : ""} /></button><Tooltip id="copy-url-tooltip" place="top-end" content={urlCopied ? "Copied!" : "Copy URL"} openEvents={isMobile ? {
              click: true
            } : {
              mouseenter: true
            }} closeEvents={isMobile ? {
              click: true
            } : {
              mouseleave: true
            }} positionStrategy="fixed" style={{
              zIndex: 10000,
              backgroundColor: "#1F2937",
              color: "white",
              opacity: 1,
              padding: "4px 8px"
            }} /></div> : <div className="tw-h-10" />}</div>;
      };
      if (!shouldRender) {
        t35 = null;
        break bb0;
      }
      t33 = `tailwind-scope tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/70 tw-p-2 tw-transition-opacity tw-duration-200 sm:tw-p-4 ${isVisible ? "tw-opacity-100" : "tw-opacity-0"}`;
      if ($[69] !== onClose) {
        t34 = <button type="button" aria-label="Close share modal" className="tw-absolute tw-inset-0 tw-border-0 tw-bg-transparent" onClick={onClose} />;
        $[69] = onClose;
        $[70] = t34;
      } else {
        t34 = $[70];
      }
      t26 = dialogRef;
      t27 = true;
      t28 = -1;
      t29 = "true";
      t30 = "header-share-title";
      t31 = "header-share-modal";
      t32 = `tw-relative tw-flex tw-w-full tw-max-w-md tw-flex-col tw-overflow-y-auto tw-rounded-xl tw-border tw-border-iron-700 tw-bg-iron-950 tw-text-left tw-shadow-xl tw-transition-all tw-duration-200 ${isVisible ? "tw-translate-y-0 tw-scale-100 tw-opacity-100" : "tw-translate-y-1 tw-scale-95 tw-opacity-0"}`;
      t22 = "tw-flex tw-flex-col tw-gap-2";
      if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
        t23 = <h2 id="header-share-title" className="tw-sr-only">Share</h2>;
        $[71] = t23;
      } else {
        t23 = $[71];
      }
      let t37;
      if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
        t37 = getRefreshToken();
        $[72] = t37;
      } else {
        t37 = $[72];
      }
      let t38;
      if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
        t38 = (tab, subTab) => {
          setActiveTab(tab);
          setActiveSubTab(subTab);
        };
        $[73] = t38;
      } else {
        t38 = $[73];
      }
      if ($[74] !== activeSubTab || $[75] !== activeTab) {
        t24 = <ModalMenu isShareConnection={!!t37} activeTab={activeTab} activeSubTab={activeSubTab} onTabChange={t38} />;
        $[74] = activeSubTab;
        $[75] = activeTab;
        $[76] = t24;
      } else {
        t24 = $[76];
      }
      t25 = printImage();
    }
    $[47] = activeSubTab;
    $[48] = activeTab;
    $[49] = getDisplayContent;
    $[50] = isMobile;
    $[51] = isVisible;
    $[52] = onClose;
    $[53] = shouldRender;
    $[54] = urlCopied;
    $[55] = t22;
    $[56] = t23;
    $[57] = t24;
    $[58] = t25;
    $[59] = t26;
    $[60] = t27;
    $[61] = t28;
    $[62] = t29;
    $[63] = t30;
    $[64] = t31;
    $[65] = t32;
    $[66] = t33;
    $[67] = t34;
    $[68] = t35;
  } else {
    t22 = $[55];
    t23 = $[56];
    t24 = $[57];
    t25 = $[58];
    t26 = $[59];
    t27 = $[60];
    t28 = $[61];
    t29 = $[62];
    t30 = $[63];
    t31 = $[64];
    t32 = $[65];
    t33 = $[66];
    t34 = $[67];
    t35 = $[68];
  }
  if (t35 !== Symbol.for("react.early_return_sentinel")) {
    return t35;
  }
  let t36;
  if ($[77] !== t22 || $[78] !== t23 || $[79] !== t24 || $[80] !== t25) {
    t36 = <div className={t22}>{t23}{t24}{t25}</div>;
    $[77] = t22;
    $[78] = t23;
    $[79] = t24;
    $[80] = t25;
    $[81] = t36;
  } else {
    t36 = $[81];
  }
  let t37;
  if ($[82] !== t26 || $[83] !== t27 || $[84] !== t28 || $[85] !== t29 || $[86] !== t30 || $[87] !== t31 || $[88] !== t32 || $[89] !== t36) {
    t37 = <dialog ref={t26} open={t27} tabIndex={t28} aria-modal={t29} aria-labelledby={t30} data-testid={t31} className={t32}>{t36}</dialog>;
    $[82] = t26;
    $[83] = t27;
    $[84] = t28;
    $[85] = t29;
    $[86] = t30;
    $[87] = t31;
    $[88] = t32;
    $[89] = t36;
    $[90] = t37;
  } else {
    t37 = $[90];
  }
  let t38;
  if ($[91] !== t33 || $[92] !== t34 || $[93] !== t37) {
    t38 = <div className={t33}>{t34}{t37}</div>;
    $[91] = t33;
    $[92] = t34;
    $[93] = t37;
    $[94] = t38;
  } else {
    t38 = $[94];
  }
  return t38;
}
function _temp2(url) {
  return <div className="tw-flex tw-items-center tw-gap-2" style={squareStyle}><a href={url} className="decoration-none tw-flex tw-flex-col tw-items-center tw-gap-8"><Image unoptimized={true} priority={true} loading="eager" src="/6529Core.png" alt="6529 Desktop" width={150} height={150} className="unselectable" /><div className="tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-bg-iron-200 tw-px-4 tw-py-3 tw-text-iron-900"><FontAwesomeIcon icon={faExternalLink} /><div className="no-wrap">Open in 6529 Desktop</div></div></a></div>;
}
function _temp(src, alt) {
  const normalizedSrc = src?.trim();
  return <div className="tw-relative tw-h-full tw-w-full">{normalizedSrc ? <Image unoptimized={true} priority={true} loading="eager" src={normalizedSrc} alt={alt} fill={true} sizes="(max-width: 768px) 92vw, 28rem" className="unselectable tw-object-contain" /> : <div className="tw-h-full tw-w-full tw-animate-pulse tw-rounded-md tw-bg-iron-900/40" />}</div>;
}
function ModalMenu(t0) {
  const $ = _c(61);
  const {
    isShareConnection,
    activeTab,
    activeSubTab,
    onTabChange
  } = t0;
  const isElectron = useElectron() ?? false;
  const topTabCount = isShareConnection ? 3 : 2;
  let t1;
  if ($[0] !== activeTab || $[1] !== isElectron) {
    t1 = getSubTabCount(activeTab, isElectron);
    $[0] = activeTab;
    $[1] = isElectron;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const subTabCount = t1;
  let t2;
  if ($[3] !== activeTab) {
    t2 = getSubTabLabel(activeTab);
    $[3] = activeTab;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const subTabLabel = t2;
  const getMenuButtonClass = _temp3;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="tw-px-1 tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-[0.08em] tw-text-iron-500">Share Type</div>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = `repeat(${topTabCount}, minmax(0, 1fr))`;
  let t5;
  if ($[6] !== t4) {
    t5 = {
      gridTemplateColumns: t4
    };
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== activeTab || $[9] !== isShareConnection || $[10] !== onTabChange) {
    t6 = isShareConnection && <button type="button" disabled={activeTab === Mode.SHARE} className={getMenuButtonClass(activeTab === Mode.SHARE)} onClick={() => onTabChange(Mode.SHARE, SubMode.APP)}>Connection</button>;
    $[8] = activeTab;
    $[9] = isShareConnection;
    $[10] = onTabChange;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = activeTab === Mode.NAVIGATE;
  const t8 = getMenuButtonClass(activeTab === Mode.NAVIGATE);
  let t9;
  if ($[12] !== onTabChange) {
    t9 = () => onTabChange(Mode.NAVIGATE, SubMode.APP);
    $[12] = onTabChange;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t7 || $[15] !== t8 || $[16] !== t9) {
    t10 = <button type="button" disabled={t7} className={t8} onClick={t9}>Current URL</button>;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const t11 = activeTab === Mode.APPS;
  const t12 = getMenuButtonClass(activeTab === Mode.APPS);
  let t13;
  if ($[18] !== onTabChange) {
    t13 = () => onTabChange(Mode.APPS, SubMode.APP);
    $[18] = onTabChange;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== t11 || $[21] !== t12 || $[22] !== t13) {
    t14 = <button type="button" disabled={t11} className={t12} onClick={t13}>6529 Apps</button>;
    $[20] = t11;
    $[21] = t12;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t10 || $[25] !== t14 || $[26] !== t5 || $[27] !== t6) {
    t15 = <div className="tw-flex tw-flex-col tw-gap-1">{t3}<div className="tw-grid tw-gap-2" style={t5}>{t6}{t10}{t14}</div></div>;
    $[24] = t10;
    $[25] = t14;
    $[26] = t5;
    $[27] = t6;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== subTabLabel) {
    t16 = <div className="tw-px-1 tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-[0.08em] tw-text-iron-500">{subTabLabel}</div>;
    $[29] = subTabLabel;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const t17 = `repeat(${subTabCount}, minmax(0, 1fr))`;
  let t18;
  if ($[31] !== t17) {
    t18 = {
      gridTemplateColumns: t17
    };
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  const t19 = activeSubTab === SubMode.APP;
  const t20 = getMenuButtonClass(activeSubTab === SubMode.APP);
  let t21;
  if ($[33] !== activeTab || $[34] !== onTabChange) {
    t21 = () => onTabChange(activeTab, SubMode.APP);
    $[33] = activeTab;
    $[34] = onTabChange;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <span>6529 Mobile</span>;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== t19 || $[38] !== t20 || $[39] !== t21) {
    t23 = <button type="button" disabled={t19} className={t20} onClick={t21}>{t22}</button>;
    $[37] = t19;
    $[38] = t20;
    $[39] = t21;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== activeSubTab || $[42] !== activeTab || $[43] !== onTabChange) {
    t24 = activeTab === Mode.NAVIGATE && <button type="button" disabled={activeSubTab === SubMode.BROWSER} className={getMenuButtonClass(activeSubTab === SubMode.BROWSER)} onClick={() => onTabChange(activeTab, SubMode.BROWSER)}><span>Browser</span></button>;
    $[41] = activeSubTab;
    $[42] = activeTab;
    $[43] = onTabChange;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== activeSubTab || $[46] !== activeTab || $[47] !== isElectron || $[48] !== onTabChange) {
    t25 = !isElectron && <button type="button" disabled={activeSubTab === SubMode.CORE} className={getMenuButtonClass(activeSubTab === SubMode.CORE)} onClick={() => onTabChange(activeTab, SubMode.CORE)}><span>6529 Desktop</span></button>;
    $[45] = activeSubTab;
    $[46] = activeTab;
    $[47] = isElectron;
    $[48] = onTabChange;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== t18 || $[51] !== t23 || $[52] !== t24 || $[53] !== t25) {
    t26 = <div className="tw-grid tw-gap-2" style={t18}>{t23}{t24}{t25}</div>;
    $[50] = t18;
    $[51] = t23;
    $[52] = t24;
    $[53] = t25;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] !== t16 || $[56] !== t26) {
    t27 = <div className="tw-flex tw-flex-col tw-gap-1">{t16}{t26}</div>;
    $[55] = t16;
    $[56] = t26;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] !== t15 || $[59] !== t27) {
    t28 = <div className="tw-flex tw-flex-col tw-gap-2">{t15}{t27}</div>;
    $[58] = t15;
    $[59] = t27;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  return t28;
}
function _temp3(active) {
  if (active) {
    return "tw-inline-flex tw-h-10 tw-w-full tw-min-w-0 tw-items-center tw-justify-center tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-rounded-xl tw-border-0 tw-px-2 tw-text-[15px] tw-font-medium tw-transition tw-duration-200 tw-bg-iron-700 tw-text-iron-50";
  }
  return "tw-inline-flex tw-h-10 tw-w-full tw-min-w-0 tw-items-center tw-justify-center tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-rounded-xl tw-border-0 tw-px-2 tw-text-[15px] tw-font-medium tw-transition tw-duration-200 tw-bg-iron-900 tw-text-iron-400 hover:tw-bg-iron-800 hover:tw-text-iron-100";
}
function CoreAppsDownload() {
  const $ = _c(7);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [versions, setVersions] = useState(t0);
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const fetchYml = _temp4;
      const loadVersions = async () => {
        const versions_0 = [];
        const enabledConfigs = CORE_OS_CONFIGS.filter(_temp5);
        const results = await Promise.allSettled(enabledConfigs.map(config_0 => fetchYml(config_0.url)));
        results.forEach((result, index) => {
          const osConfig = enabledConfigs[index];
          if (!osConfig) {
            return;
          }
          if (result.status === "fulfilled") {
            versions_0.push({
              ...osConfig,
              version: result.value.version
            });
            return;
          }
          console.error(`Failed to fetch or process ${osConfig.displayName}:`, result.reason);
        });
        setVersions(versions_0);
      };
      loadVersions();
    };
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== versions) {
    t3 = versions.map(_temp6);
    $[3] = versions;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <div style={squareStyle}><div className="tw-inline-flex tw-flex-col tw-gap-10">{t3}</div></div>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
function _temp6(version) {
  return <CoreAppDownload key={version.name} platform={version.displayName} icon={version.image} title={version.displayName} downloadPath={version.downloadPath} version={version.version ?? ""} />;
}
function _temp5(config) {
  return config.enabled;
}
async function _temp4(url) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}`);
  }
  const text = await response.text();
  return yaml.load(text) as LatestYml;
}
function CoreAppDownload(t0) {
  const $ = _c(10);
  const {
    platform,
    icon,
    title,
    downloadPath,
    version
  } = t0;
  if (!version) {
    return null;
  }
  const url = `https://d3lqz0a4bldqgf.cloudfront.net/${downloadPath}/${version}.html`;
  let t1;
  if ($[0] !== icon || $[1] !== title) {
    t1 = <div className="tw-rounded-full tw-bg-white tw-p-4"><Image unoptimized={true} priority={true} loading="eager" src={icon} alt={title} width={40} height={40} className="unselectable" /></div>;
    $[0] = icon;
    $[1] = title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== platform || $[4] !== version) {
    t2 = <div className="tw-flex tw-w-full tw-items-center tw-gap-2"><div className="no-wrap tw-text-lg tw-font-medium">{platform} v{version}</div></div>;
    $[3] = platform;
    $[4] = version;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== url) {
    t3 = <a href={url} target="_blank" rel="noopener noreferrer" className="decoration-none tw-flex tw-w-full tw-items-center tw-gap-4 tw-rounded-lg tw-border tw-border-solid tw-border-white tw-bg-black tw-px-5 tw-py-3 tw-transition-all tw-duration-300 tw-ease-out hover:tw-scale-[1.03]">{t1}{t2}</a>;
    $[6] = t1;
    $[7] = t2;
    $[8] = url;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}